import { useCallback } from 'react';
import { useSnackbar } from '../../../../store/snackbar';
import { DropResult } from 'react-beautiful-dnd';
import { useIntl } from 'react-intl';
import { graphql } from '../../../../gql';
import { useApiMutation } from '../../../../query/hooks';
import { Phase } from '../../JourneyDetailPage';

const updatePhaseTouchpointsOrder = graphql(`
	mutation timeline_updatePhaseTouchpointsOrder($phaseId: UUID!, $touchpoints: [PhaseUpdateTouchpointsEntityRelationInput!]!) {
		updatePhase(by: { id: $phaseId }, data: { touchpoints: $touchpoints }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	state: readonly Phase[];
	setState: React.Dispatch<React.SetStateAction<readonly Phase[]>>;
}>;

export const useHandleReorderTouchpointsDetail = ({ state, setState }: Props): ((result: DropResult) => Promise<void>) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { mutateAsync: updateTouchpointsOrder } = useApiMutation(updatePhaseTouchpointsOrder, {
		onSuccess: (result) => {
			if (result.updatePhase.errorMessage) {
				console.error(result.updatePhase.errorMessage);
			}
		},
	});

	return useCallback(
		async (result: DropResult) => {
			const { source, destination } = result;
			if (destination == null) {
				openSnackbar({
					open: true,
					message: intl.formatMessage({ id: 'timeline.phase.update.error' }),
					variant: 'alert',
					alert: {
						color: 'error',
					},
					close: true,
				});
				return;
			}

			setState((prev) => {
				const newState = [...prev];

				const phaseIndex = newState.findIndex(({ id }) => destination.droppableId === id);
				const touchpoints = newState[phaseIndex].touchpoints;
				const itemToInsertToNewPosition = touchpoints[source.index];

				touchpoints.splice(source.index, 1);
				touchpoints.splice(destination.index, 0, itemToInsertToNewPosition);

				newState[phaseIndex].touchpoints = touchpoints;

				return newState;
			});

			const phase = state.filter((col) => col.id === destination.droppableId)[0];
			const phaseTouchpoints = phase.touchpoints;

			await updateTouchpointsOrder({
				phaseId: destination.droppableId,
				touchpoints: phaseTouchpoints.map((tp, idx) => ({ update: { by: { id: tp.id }, data: { order: idx + 1 } } })),
			});
		},
		[state, intl, setState, updateTouchpointsOrder, openSnackbar],
	);
};
